import routes from '@/routes/routes';
import http from '@http';
import store from '@/store';
import {GALogEvent} from '@/utils/firebase';
import socketService from '@services/socket.service';

class AuthService {
    constructor() {
        if (AuthService.instance) {
            return AuthService.instance;
        }

        this._isAuthorized = !!localStorage.getItem('authentication');

        AuthService.instance = this;
    }

    get isAuthorized() {
        return this._isAuthorized;
    }

    socialLogin(data) {
        const url = `/api/auth/callback/${data.social}/${data.action}`;

        return http.post(url, data).then(res => {
            if (res.status === 200) {
                if(res.data.type) {
                    return res.data;
                } else {
                    this._isAuthorized = true;
                    localStorage.setItem('authentication', 'done');
                    localStorage.removeItem('register_role');
                    localStorage.removeItem('register_country');

                    const proceed = document.cookie
                        .split(';')
                        .find(row => row.trim().startsWith('sso='))
                        ?.split('=')[1] ? 'sso_auth' : 'dashboard';

                    const authEvent = data.action === 'register' ? 'sign_up' : 'login';
                    this.getUserAndPermissions(data.platform, proceed, authEvent);
                }
            } else {
                console.error('res', res);
            }
        }).catch(error => {
            if (error.response.status === 404 && error.response.data.message.startsWith('No associated user found')) {
                // Set modal flag and redirect to front page
                const email = error.response.data.message.split(': ').pop();
                localStorage.setItem('social_login_no_user', email);
            } else if (error.response.status !== 200) {
                console.error('res', error.response.status, error.response.data.message);
                localStorage.setItem('social_login_error', error.response.data.message);
            }
            location.reload();
        });
    }

    socialLoginHandled() {
        localStorage.setItem('authentication', 'done');
    }

    registerUser(data) {
        return http.post('/api/auth/register', data).then(res => {
            if (res.status === 200) {
                GALogEvent('sign_up', {method: 'password'});
                localStorage.removeItem('register_role');
                localStorage.removeItem('register_country');
                return res.data;
            } else {
                console.error('reg error', res);
            }
        });
    }

    /**
     * This is triggered from employee user register invite and will create a verified user
     */
    registerEmployeeUser(data) {
        return http.post('/api/auth/verification/register', data).then(res => {
            GALogEvent('sign_up', {method: 'password'});
            return res.data;
        });
    }

    login(data) {
        return http.post('/api/auth/login', data).then(res => {
            if (res.status === 200) {
                if(res.data.type) {
                    return res.data;
                } else {
                    this._isAuthorized = true;
                    localStorage.setItem('authentication', 'done');
                    const proceed = routes.currentRoute.name === 'sso_auth' ? '' : 'dashboard';
                    this.getUserAndPermissions(data.platform, proceed, 'login');
                }
            }
        }).catch(error => {
            store.dispatch('STOP_LOGIN_LOADER');
            if(error.status === 202) {
                return error.data.message;
            }
        });
    }

    login2FA(data) {
        return http.post('/api/auth/login-2fa', data).then(res => {
            this._isAuthorized = true;
            localStorage.setItem('authentication', 'done');
            return res.data;
        }).catch(error => {
            store.dispatch('STOP_LOGIN_LOADER');
            if(error.status === 202) {
                return error.data.message;
            }
        });
    }

    getUserAndPermissions(platform, proceed = 'dashboard', authEvent = null) {
        store.dispatch('GET_USER', platform).then(() => {
            if (store.getters.user && store.getters.user.email_verified_at) {
                if (authEvent) {
                    GALogEvent(authEvent, {method: platform?.login});
                }
                store.dispatch('GET_PERMISSIONS').then(() => {
                    if (proceed === 'dashboard') {
                        store.dispatch('GET_USER_HEADER_INFO');
                        routes.push('/dashboard').catch(() => {
                            /* NavigationDuplicated is expected. */
                        });
                    } else if (proceed === 'sso_auth') {
                        routes.push('/sso/auth');
                    }
                });
            }
            // else {
            //     console.log('email not verified');
            // }
        });
    }

    loginAs(id) {
        http.get(`/api/admin/login/${id}`).then(() => {
            clearActiveContext();
            localStorage.setItem('login_as', 'done');
            window.location = '/dashboard';
        });
    }

    logout(redirectPath) {
        if (!this._isAuthorized || !localStorage.getItem('authentication')) {
            location.reload();
            return;  //location.reload(); before login reload bad, after login reload good when suddenly unauthenticated
        }

        this._isAuthorized = false;
        socketService.disconnect();

        http.get('/api/auth/logout', {params:{platform: localStorage.getItem('platform')}})
            .catch(() => console.error('Already logged out')).finally(() => {
                clearStorage();
                if (redirectPath) {
                    routes.replace(redirectPath, () => location.reload());
                } else {
                    location.reload();
                }
            });
    }

    reloadEnvironment() {
        if(this._isAuthorized) {
            this._isAuthorized = false;
            socketService.disconnect();
            clearStorage();
            location.reload();
        }
    }

    /**
     * Do a reload after calling this!
     * @param id
     * @returns {Promise<AxiosResponse<any> | void>}
     */
    logoutAs(id) {
        return http.post(`/api/admin/logout/${id}`).then(() => {
            clearActiveContext();
            localStorage.removeItem('login_as');
        }).catch(() => console.error('Logout failed'));
    }

    requestForgotPassword(data) {
        return http.post('/api/auth/password/email', data).then(res => res.data);
    }

    resendEmailVerification(username) {
        return http.post('/api/auth/verification/resend', username).then(res => res.data);
    }

    resetPassword(data) {
        return http.post('/api/auth/password/reset', {
                email: data.email,
                token: data.token,
                password: data.password,
                password_confirmation: data.password_confirmation
            }).then(res => {
                if (res.status === 200) {
                    return res.data;
                }
                // else {
                //     console.log(res);
                // }
            });
    }

    verifyResetToken(data) {
        return http.post('/api/auth/password/verify', data).then(res => res.data);
    }
    recoverAccount(data) {
        return http.post('/api/auth/recover-2fa', data).then(res => res.data);
    }
}

function clearStorage() {
    const eliisPopupClosed = localStorage.getItem('eliisPopupClosed');
    const buildVersion = localStorage.getItem('buildVersion');
    localStorage.clear();
    buildVersion && localStorage.setItem('buildVersion', buildVersion);
    eliisPopupClosed && localStorage.setItem('eliisPopupClosed', eliisPopupClosed);
}

function clearActiveContext() {
    ['kindergarten_id', 'organization_id', 'supplier_id', 'official_kindergarten_id', 'employee_course_id'].forEach(key => localStorage.removeItem(key));
}

export default new AuthService();
